import type { RepositoryType } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { Checkbox } from '../input/checkbox.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import { ProjectChip } from './project-chip.tsx'
import { eyebrow } from './project-dialog.css.ts'
import * as styles from './repository-pick-list.css.ts'

export type PickableRepository = {
  id: string
  fullName: string
  type: RepositoryType
  /** The project the repository is in today, when it has one. */
  currentProjectName?: string
}

export type RepositoryPickListProps = {
  /** The heading over the list: what these candidates are. */
  label: string
  candidates: PickableRepository[]
  pickedIds: ReadonlySet<string>
  onToggle: (repositoryId: string) => unknown
  /**
   * When set, every row that is not already picked goes dead and this says why — the selection cap's
   * "Up to 100 at a time.". Checked rows stay live so the reader can trade one pick for another.
   */
  unpickedDisabledReason?: string
}

/** A striped multi-select of repositories, each with the project it would be taken from. */
export const RepositoryPickList: React.FC<RepositoryPickListProps> = ({
  label,
  candidates,
  pickedIds,
  onToggle,
  unpickedDisabledReason,
}) => {
  const reasonId = `pick-list-reason-${label.replace(/\W+/g, '-').toLowerCase()}`
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p className={eyebrow}>{label}</p>
        <span className={styles.count}>
          {candidates.length} {pluralize(candidates.length, 'repository', 'repositories')}
        </span>
      </div>
      {unpickedDisabledReason && (
        <p className={styles.capNote} id={reasonId} role="status">
          {unpickedDisabledReason}
        </p>
      )}
      <div className={styles.picker} role="group" aria-label={label}>
        {candidates.map(repository => {
          const checkboxId = `pick-${repository.id}`
          const isPicked = pickedIds.has(repository.id)
          const isDisabled = unpickedDisabledReason !== undefined && !isPicked
          return (
            <div key={repository.id} className={styles.row}>
              <Checkbox
                id={checkboxId}
                checked={isPicked}
                state={isDisabled ? 'disabled' : 'default'}
                onCheckedChange={() => onToggle(repository.id)}
                ariaLabel={`Select ${repository.fullName}`}
                {...(isDisabled ? { 'aria-describedby': reasonId } : {})}
              />
              <label className={isDisabled ? styles.nameDisabled : styles.name} htmlFor={checkboxId}>
                <SCMIcon type={repository.type} size="medium" />
                <span className={styles.nameText}>{repository.fullName}</span>
              </label>
              <span className={styles.spacer} />
              {repository.currentProjectName && <ProjectChip name={repository.currentProjectName} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}
